import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {
  Button,
  Column,
  Loader,
  MeatballMenu,
  Row,
  Text,
  useDesignTokens,
} from "@gradience/ui";
import Icon, { IconContainer } from "@gradience/ui/dist/components/icon";
import {
  deleteApi,
  getPostMutationFn,
  getQueryOptions,
  postApi,
  putApi,
  useApiDelete,
  useApiPost,
} from "../../../../../lib/api";
import { useParams } from "@tanstack/react-router";
import PageChrome from "../../../../../components/page-chrome";
import { useTestScanPages } from "./utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";

type ReviewGradingSearch = {
  pageIndex: number;
};
export const Route = createFileRoute("/$test-slug/groups/$id/review/")({
  component: ReviewScans,
  validateSearch: (search) => {
    const validatedSearch: ReviewGradingSearch = {
      pageIndex: 0,
    };

    if (
      search.pageIndex &&
      typeof search.pageIndex === "number" &&
      search.pageIndex >= 0
    ) {
      validatedSearch.pageIndex = search.pageIndex;
    }
    return validatedSearch;
  },
});

const CIRCLE_PADDING = 35;
const NATURAL_PAGE_RATIO = 0.7727272727; // 8.5 / 11

function ReviewScans() {
  const designTokens = useDesignTokens();
  const params = useParams({
    from: "/$test-slug/groups/$id/review/",
  });
  const { pageIndex } = useSearch({
    from: "/$test-slug/groups/$id/review/",
  });
  const testScanPagesQuery = useTestScanPages();
  const groupQuery = useQuery(
    getQueryOptions({
      endpoint: "/groups/:id",
      pathParams: {
        id: params.id,
      },
    })
  );
  const answerCircles = useQuery({
    ...getQueryOptions({
      endpoint: "/test-scan-pages/:id/questions",
      pathParams: {
        id: testScanPagesQuery.data?.data[pageIndex]?.id!,
      },
    }),
    enabled: testScanPagesQuery.data?.data[pageIndex]?.id !== undefined,
  });

  const [imageNaturalWidth, setImageNaturalWidth] = useState<number | null>(
    null
  );
  const [imageWidth, setImageWidth] = useState<number | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const scalingFactor =
    imageWidth && imageNaturalWidth ? imageWidth / imageNaturalWidth : 1;

  const currentTestScanPageId = testScanPagesQuery.data?.data[pageIndex]?.id;
  const students = useQuery(
    getQueryOptions({
      endpoint: "/students",
      queryParams: {
        groupId: params.id,
      },
    })
  );

  const issuesQueryOptions = useMemo(
    () =>
      getQueryOptions({
        endpoint: "/grading-issues",
        queryParams: {
          groupId: params.id,
        },
      }),
    [params.id]
  );

  const issues = useQuery(issuesQueryOptions);
  const [issueText, setIssueText] = useState<string>();
  const queryClient = useQueryClient();
  const hasNextPage = Boolean(
    testScanPagesQuery.data?.data.length &&
      testScanPagesQuery.data?.data.length > pageIndex + 1
  );
  const hasPreviousPage = pageIndex > 0;

  const createIssue = useMutation({
    mutationFn: ({
      issueText,
      pageNumber,
      studentId,
      testScanPageId,
    }: {
      issueText: string;
      pageNumber: number;
      studentId: string;
      testScanPageId: string;
    }) =>
      postApi({
        endpoint: "/grading-issues",
        body: {
          pageNumber,
          entirePage: true,
          studentId,
          groupId: params.id,
          issueType: "OTHER",
          comment: issueText,
          testScanPageId,
        },
      }),
    onError: (_err, _newSettings, context) => {
      window.alert("Error recording issue. Please try again.");
      if (context) {
        queryClient.setQueryData(issuesQueryOptions.queryKey, context as any);
      }
    },
    onMutate: (body) => {
      queryClient.cancelQueries({ queryKey: issuesQueryOptions.queryKey });
      const previousData = queryClient.getQueryData(
        issuesQueryOptions.queryKey
      );

      queryClient.setQueryData(issuesQueryOptions.queryKey, (_old) => {
        const old = _old ?? { data: [], count: 0 };
        return {
          ...old,
          data: [
            {
              id: crypto.randomUUID(),
              pageNumber: body.pageNumber,
              studentId: body.studentId,
              comment: body.issueText,
              issueType: "OTHER" as const,
              entirePage: true,
              createdAt: new Date().toISOString(),
              resolved: false,
              groupId: params.id,
            },
            ...(old.data ?? []),
          ],
        };
      });

      return previousData;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: issuesQueryOptions.queryKey });
    },
  });
  const deleteIssue = useMutation({
    mutationFn: async (issueId: string) => {
      await deleteApi({
        endpoint: "/grading-issues/:id",
        pathParams: {
          id: issueId,
        },
      });
    },
    onMutate: (issueId) => {
      queryClient.cancelQueries({ queryKey: issuesQueryOptions.queryKey });
      const previousData = queryClient.getQueryData(
        issuesQueryOptions.queryKey
      );

      queryClient.setQueryData(issuesQueryOptions.queryKey, (_old) => {
        const old = _old ?? { data: [], count: 0 };
        return {
          ...old,
          data: old.data?.filter((issue) => issue.id !== issueId),
        };
      });

      return previousData;
    },
    onError: (_err, _newSettings, context) => {
      window.alert("Error deleting issue. Please try again.");
      if (context) {
        queryClient.setQueryData(issuesQueryOptions.queryKey, context);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: issuesQueryOptions.queryKey });
    },
  });
  const setGroupReviewed = useMutation({
    mutationFn: () =>
      putApi({
        endpoint: "/groups/:id",
        pathParams: {
          id: params.id,
        },
        body: {
          isReviewed: true,
        },
      }),
  });

  const navigate = useNavigate({
    from: "/$test-slug/groups/$id/review",
  });

  const reset = () => {
    setImageNaturalWidth(null);
    setImageWidth(null);
    setIssueText(undefined);
  };

  const imageLoading = answerCircles.isPending || testScanPagesQuery.isPending;

  return (
    <PageChrome>
      <Column style={{ gap: 40, flex: 1 }}>
        <Row
          gap={16}
          style={{
            alignItems: "flex-start",
          }}
        >
          <Row
            gap={8}
            style={{
              alignItems: "flex-start",
              flex: 1,
            }}
          >
            <Icon
              name="arrow-left"
              to={`/${params["test-slug"]}/groups/${params.id}`}
            />
            <Column gap={8}>
              <Text textStyle="32px - Bold">Review Grading</Text>
              <Text textStyle="14px - Semibold">
                {groupQuery.data?.name} • Page {pageIndex + 1} of{" "}
                {testScanPagesQuery.data?.data.length}
              </Text>
            </Column>
          </Row>
          {hasPreviousPage && (
            <Button
              text="Previous"
              style={{ display: "block" }}
              loading={setGroupReviewed.isPending}
              onPress={() => {
                reset();
                navigate({
                  search: {
                    pageIndex: pageIndex - 1,
                  },
                });
              }}
            />
          )}
          {hasNextPage ? (
            <Button
              variant="primary"
              loading={setGroupReviewed.isPending}
              text="Next"
              style={{ display: "block" }}
              onPress={() => {
                reset();
                navigate({
                  search: {
                    pageIndex: pageIndex + 1,
                  },
                });
              }}
            />
          ) : (
            <Button
              variant="primary"
              loading={setGroupReviewed.isPending}
              text="Finish"
              style={{ display: "block" }}
              onPress={async () => {
                await setGroupReviewed.mutateAsync();
                navigate({
                  to: "/$test-slug/groups/$id",
                });
              }}
            />
          )}
        </Row>
        <Row
          style={{
            gap: 20,
            alignItems: "flex-start",
          }}
        >
          <Column
            style={{
              padding: 24,
              border: `1px solid ${designTokens.colors.border.default}`,
              gap: 24,
              borderRadius: 16,
              backgroundColor: designTokens.colors.surface.Background,
              flex: 1,
            }}
          >
            <Row style={{ gap: 16, alignItems: "center" }}>
              <IconContainer icon="file-search-01" />
              <Text textStyle="20px - Bold">Review Test Grading</Text>
            </Row>
            <Column
              style={{
                borderRadius: 12,
                border: `1px solid ${designTokens.colors.border.default}`,
                backgroundColor: designTokens.colors.surface.Subdued,
                overflow: "hidden",
                position: "relative",
                // Only if the image is not loaded
                aspectRatio: imageNaturalWidth
                  ? undefined
                  : `${NATURAL_PAGE_RATIO} / 1`,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {imageLoading ? (
                <Loader />
              ) : (
                <>
                  <img
                    onLoad={(event) => {
                      setImageNaturalWidth(event.currentTarget.naturalWidth);
                      setImageWidth(event.currentTarget.width);
                    }}
                    ref={imageRef}
                    alt="test-scan"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "cover",
                    }}
                    src={testScanPagesQuery.data?.data[pageIndex].imageUrl}
                  />
                  {answerCircles.data?.data.map((circle) =>
                    circle.selectedAnswer !== undefined ? (
                      <span
                        style={{
                          position: "absolute",
                          top:
                            (circle.selectedAnswerY! - CIRCLE_PADDING) *
                            scalingFactor,
                          left:
                            (circle.selectedAnswerX! - CIRCLE_PADDING) *
                            scalingFactor,
                          width:
                            (circle.selectedAnswerSize! + CIRCLE_PADDING * 2) *
                            scalingFactor,
                          height:
                            (circle.selectedAnswerSize! + CIRCLE_PADDING * 2) *
                            scalingFactor,
                          borderRadius: "50%",
                          border: `${15 * scalingFactor}px solid ${designTokens.colors.misc.Brand}`,
                          boxSizing: "border-box",
                        }}
                      />
                    ) : null
                  )}
                </>
              )}
            </Column>
          </Column>
          <Column
            style={{
              gap: 16,
              flexGrow: 0,
              flexShrink: 0,
              flexBasis: 367,
            }}
          >
            <Column
              style={{
                padding: 24,
                border: `1px solid ${designTokens.colors.border.default}`,
                gap: 24,
                borderRadius: 16,
                backgroundColor: designTokens.colors.surface.Background,
              }}
            >
              <Row style={{ gap: 16, alignItems: "center" }}>
                <IconContainer icon="alert-square" />
                <Text textStyle="20px - Bold">Flag an issue</Text>
              </Row>
              <Column gap={8}>
                <Text textStyle="18px - Bold">Flag for review</Text>
                <Text textStyle="14px - Medium">
                  If you see an issue with the grading system, please describe
                  the issue below and we will review it.
                </Text>
              </Column>
              <form
                style={{ display: "flex", gap: 8, flexDirection: "column" }}
                onSubmit={(e) => {
                  console.log("SUBMITTED");
                  e.preventDefault();

                  if (!currentTestScanPageId) {
                    return;
                  }

                  const currentTestScanPage =
                    testScanPagesQuery.data?.data.find(
                      (page) => page.id === currentTestScanPageId
                    );

                  if (!currentTestScanPage || !currentTestScanPage.studentId) {
                    return;
                  }

                  createIssue.mutate({
                    pageNumber: currentTestScanPage.pageNumber,
                    studentId: currentTestScanPage.studentId,
                    issueText: issueText ?? "",
                    testScanPageId: currentTestScanPageId,
                  });
                  setIssueText(undefined);
                }}
              >
                <textarea
                  onKeyDown={(event) => {
                    // Submit form on enter
                    if (event.key === "Enter" && !event.shiftKey) {
                      console.log("submit");
                      event.preventDefault();
                      console.log(event.currentTarget.form);
                      event.currentTarget.form?.requestSubmit();
                    }
                  }}
                  value={issueText ?? ""}
                  onChange={(event) => {
                    setIssueText(event.target.value);
                  }}
                  style={{
                    width: "100%",
                    height: 150,
                    padding: 16,
                    borderRadius: 12,
                    border: `1px solid ${designTokens.colors.border.default}`,
                    resize: "none",
                    boxSizing: "border-box",
                    ...designTokens.typography["14px - Medium"],
                  }}
                  placeholder="Please describe the issue here..."
                />
                <Button
                  variant="primary"
                  text="Add Issue"
                  type="submit"
                  style={{ alignSelf: "flex-start" }}
                />
              </form>
            </Column>
            {(issues.data?.data.length ?? 0) > 0 && (
              <Column
                style={{
                  padding: 24,
                  border: `1px solid ${designTokens.colors.border.default}`,
                  gap: 16,
                  borderRadius: 16,
                  backgroundColor: designTokens.colors.surface.Background,
                }}
              >
                <Row style={{ gap: 16, alignItems: "center" }}>
                  <IconContainer icon="alert-square" />
                  <Text textStyle="20px - Bold">Issues</Text>
                </Row>
                <Column gap={8}>
                  {issues.data?.data.map((issue) => {
                    const student = students.data?.data.find(
                      (s) => s.id === issue.studentId
                    );

                    if (!student) {
                      return null;
                    }

                    return (
                      <Row
                        key={issue.id}
                        style={{
                          gap: 8,
                          borderRadius: 12,
                          backgroundColor: designTokens.colors.surface.Subdued,
                          padding: 16,
                        }}
                      >
                        <Column style={{ gap: 8, flex: 1 }}>
                          <Text textStyle="16px - Semibold">
                            {student.firstName} {student.lastName} • Page{" "}
                            {issue.pageNumber}
                          </Text>
                          <Text
                            textStyle="14px - Medium"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxLines: 1,
                              textWrap: "nowrap",
                              maxWidth: 240,
                            }}
                          >
                            {issue.comment}
                          </Text>
                        </Column>
                        <MeatballMenu
                          options={[
                            { label: "Delete", value: "delete" },
                            { label: "Go to page", value: "go-to-page" },
                          ]}
                          onSelect={(option) => {
                            switch (option) {
                              case "delete":
                                deleteIssue.mutate(issue.id);
                                break;
                              case "go-to-page":
                                const newPageIndex =
                                  testScanPagesQuery.data?.data.findIndex(
                                    (page) =>
                                      page.studentId === issue.studentId &&
                                      page.pageNumber === issue.pageNumber
                                  )!;
                                if (
                                  newPageIndex === -1 ||
                                  newPageIndex === pageIndex
                                ) {
                                  return;
                                }
                                reset();
                                navigate({
                                  search: {
                                    pageIndex: newPageIndex,
                                  },
                                });
                                break;
                            }
                          }}
                        />
                      </Row>
                    );
                  })}
                </Column>
              </Column>
            )}
          </Column>
        </Row>
      </Column>
    </PageChrome>
  );
}
